import React, { useRef, useEffect, useState } from "react"
import ReactDOM from "react-dom"
import mapboxgl from "mapbox-gl"
import MapboxGeocoder from "@mapbox/mapbox-gl-geocoder"
import "mapbox-gl/dist/mapbox-gl.css"
import "@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css"
import * as turf from "@turf/turf"
// Css import
import "./styles/heatmapSidebar.css"
import "../pages/Nest/MapHome/Home.css"

import HexMapAnalytics from "../components/HexMapAnalytics"
import { HexagonControl } from "../../utils/mapControls"
import { useLoader } from "../../hooks"

mapboxgl.workerClass =
  // eslint-disable-next-line import/no-webpack-loader-syntax
  require("worker-loader!mapbox-gl/dist/mapbox-gl-csp-worker").default

const MAPBOX_TOKEN =
  "pk.eyJ1IjoiYmFsamVldHJlYWN0IiwiYSI6ImNreW54ZmI2ZjNieGUybnFwNnd1aXRudTEifQ.a0Yo3o" +
  "2Cy1mID1653pidRA"

const HeatMap = () => {
  const mapContainer = useRef(null)
  const map = useRef(null)
  const firstRender = useRef(true)
  const [renderLoader, showLoader, hideLoader] = useLoader()

  const [lastSidebarKey, setLastSidebarKey] = useState("base")
  const [sidebarKey, setSidebarKey] = useState("base")

  const [filterType, setFilterType] = useState("")
  const [popup, setPopup] = useState(null)

  const createSource = data => ({ type: "geojson", data })

  const updateSidebarInfo = newKey => {
    setLastSidebarKey(sidebarKey)
    setSidebarKey(newKey)
  }

  const typeFilter = type => {
    setFilterType(type)
  }

  let hexagonGeoData = {}
  let centersGeoData = {}

  useEffect(() => {
    async function renderMap() {
      mapboxgl.accessToken = MAPBOX_TOKEN

      map.current = new mapboxgl.Map({
        container: mapContainer.current,
        zoom: 6,
        center: [-80.191788, 25.761681],
        style: "mapbox://styles/mapbox/dark-v10"
      })

      // Controls
      const locateUser = new mapboxgl.GeolocateControl({
        positionOptions: {
          enableHighAccuracy: true
        },

        trackUserLocation: true,

        showUserHeading: true
      })

      const searchLocation = new MapboxGeocoder({
        accessToken: MAPBOX_TOKEN,
        mapboxgl: mapboxgl,
        getItemValue: ({ center, place_name }) => {
          fetch(
            "https://api.mapbox.com/geocoding/v5/mapbox.places/" +
              center[0] +
              "," +
              center[1] +
              ".json?place_type=address&access_token=" +
              MAPBOX_TOKEN
          )
            .then(response => {
              if (response.status >= 400 && response.status < 600) {
                throw new Error("Bad response from server")
              }
              return response.json()
            })
            .then(returnedResponse => {})
            .catch(error => {
              console.log(error.message, "error")
            })
          return place_name
        }
      })

      map.current.addControl(searchLocation, "top-left")
      map.current.addControl(locateUser, "top-left")
      map.current.addControl(new mapboxgl.NavigationControl(), "bottom-left")
      map.current.addControl(
        new HexagonControl({
          available: () => typeFilter("available"),
          occupied: () => typeFilter("occupied"),
          blocked: () => typeFilter("blocked"),
          all: () => typeFilter("")
        }),
        "bottom-right"
      )

      showLoader()

      let hexGridResponse = await fetch("./usaCitiesHexGrid.json")
      hexagonGeoData = await hexGridResponse.json()

      let centerGridResponse = await fetch("./usaCitiesCenterGrid.json")
      centersGeoData = await centerGridResponse.json()

      map.current.on("load", () => {
        !map.current.getSource("hexagonGeoData") &&
          map.current.addSource("hexagonGeoData", createSource(hexagonGeoData))

        !map.current.getSource("centersGeoData") &&
          map.current.addSource("centersGeoData", createSource(centersGeoData))

        !map.current.getLayer("hexagonGrid") &&
          map.current.addLayer({
            id: "hexagonGrid",
            type: "fill",
            source: "hexagonGeoData",
            minzoom: 0,
            maxzoom: 24,
            paint: {
              "fill-color": {
                property: "type",
                type: "categorical",
                stops: [
                  ["available", "#35c9bb"],
                  ["occupied", "#ae37eb"],
                  ["blocked", "#440bcc"]
                ]
              },
              "fill-outline-color": "white",
              "fill-opacity": 0.25
            }
          })
        hideLoader()
      })

      // Click Handler for showing popup
      map.current.on("click", "hexagonGrid", e => {
        let props = e.features[0].properties
        props.geometry = e.features[0].geometry
        props.center = centersGeoData.features[props.hexIndex]
        fetch(
          "https://api.mapbox.com/geocoding/v5/mapbox.places/" +
            props.center.geometry.coordinates[0] +
            "," +
            props.center.geometry.coordinates[1] +
            ".json?place_type=address&access_token=" +
            MAPBOX_TOKEN
        )
          .then(res => {
            return res.json()
          })
          .then(response => {
            let place = response.features.find(feature =>
              feature.id.includes("address")
            )
            if (place) {
              props.place = place
            }
            setPopup(
              new mapboxgl.Popup()
                .setLngLat({
                  lng: props.center.geometry.coordinates[0],
                  lat: props.center.geometry.coordinates[1]
                })
                // .setHTML(Popup(props))
                .setHTML('<div id="test"></div>')
                .addTo(map.current)
            )

            setTimeout(() => {
              ReactDOM.render(
                <Popup values={props} />,
                document.getElementById("test")
              )
            }, 20)
          })
      })

      map.current.on("mouseenter", "hexagonGrid", () => {
        map.current.getCanvas().style.cursor = "pointer"
      })

      map.current.on("mouseleave", "hexagonGrid", () => {
        map.current.getCanvas().style.cursor = ""
      })
    }

    renderMap()
  }, [])

  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false
      return
    }

    async function layerUpdate() {
      popup && popup.remove()
      setPopup(null)
      showLoader()
      let hexGridResponse = await fetch("./usaCitiesHexGrid.json")
      hexagonGeoData = await hexGridResponse.json()

      let centerGridResponse = await fetch("./usaCitiesCenterGrid.json")
      centersGeoData = await centerGridResponse.json()

      let filteredCenters = turf.featureCollection(
        filterType
          ? centersGeoData.features.filter(
              feature => feature.properties.type === filterType
            )
          : centersGeoData.features
      )

      // Updating Centers Layer
      map.current.getSource("centersGeoData").setData(filteredCenters)

      let filteredHexagons = turf.featureCollection(
        filterType
          ? hexagonGeoData.features.filter(
              feature => feature.properties.type === filterType
            )
          : hexagonGeoData.features
      )

      map.current.getSource("hexagonGeoData").setData(filteredHexagons)
      hideLoader()
    }

    map.current.getSource("hexagonGeoData") &&
      map.current.getSource("centersGeoData") &&
      layerUpdate()
  }, [filterType])

  return (
    <div>
      <HexMapAnalytics
        sidebarKey={sidebarKey}
        lastSidebarKey={lastSidebarKey}
        clickHandler={updateSidebarInfo}
      />
      <div
        ref={mapContainer}
        className="map-container map-container-wrap"></div>
      {renderLoader()}
    </div>
  )
}

export default HeatMap

const Popup = ({ values }) => {
  return (
    <>
      <p style={{ fontSize: 18, color: "black" }}>Hexagon Details: </p>
      <b>Type:</b>&nbsp; {values.type}
      <br />
      <b>No. of points:</b>&nbsp;{" "}
      {JSON.parse(values?.pointsIndex ?? "false")?.length ?? 0}
      <br />
      <b>Center:</b>&nbsp;
      {values.center.geometry.coordinates.map((point, i) => (
        <React.Fragment key={i}>{point.toPrecision(6)}</React.Fragment>
      ))}
      <br />
      <b>Description:</b>&nbsp;
      {values.place ? values.place.place_name : "Not found"}
      <br />
      {values.type === "available" ? (
        <button
          className="gradient-box"
          style={{ margin: "auto", border: "none", color: "white" }}
          onClick={() => {
            console.log("Hello")
          }}>
          Register
        </button>
      ) : null}
    </>
  )
}
